/**
 * Trends tab (SA-215) — cross-run failure-category and outcome trends
 * over cached verdicts (GET /trends; no LLM). Coverage rendering rule
 * (issue AC): a category renders as zero only where coverage > 0; an
 * uncovered bucket draws NO bars — its emptiness means "unknown", made
 * visible by the coverage strip aligned beneath the charts.
 */
import { useState } from 'react'
import { ResponsiveBar, type BarDatum } from '@nivo/bar'

import { Input } from '@/components/ui'
import {
  useNivoTheme,
  useChartColors,
} from '@/components/marketing/visualizations/nivoTheme'
import { usePersonas } from '@/hooks/usePersonas'
import { useAnalysisTrends } from '@/hooks/useConversationAnalysis'
import { analysisErrorMessage, CHANNEL_OPTIONS, toDateInput } from './runFormat'
import type { TrendBucket } from '@/types/conversationAnalysis'

type BarRow = { bucket: string; covered: boolean } & Record<string, unknown>

export function toCategoryBarData(
  buckets: TrendBucket[], categories: string[],
): BarRow[] {
  return buckets.map((b) => {
    const covered = (b.coverage ?? 0) > 0
    return {
      bucket: b.start,
      covered,
      ...Object.fromEntries(
        categories.map((c) => [c, covered ? (b.failure_categories[c] ?? 0) : 0]),
      ),
    }
  })
}

const OUTCOME_KEYS = ['successful', 'unsuccessful', 'inconclusive']

export function toOutcomeBarData(buckets: TrendBucket[]): BarRow[] {
  return buckets.map((b) => {
    const covered = (b.coverage ?? 0) > 0
    return {
      bucket: b.start,
      covered,
      ...Object.fromEntries(
        OUTCOME_KEYS.map((k) => [k, covered ? (b.outcomes[k] ?? 0) : 0]),
      ),
    }
  })
}

function coverageState(b: TrendBucket): 'covered' | 'uncovered' | 'empty' {
  if (b.conversation_count === 0) return 'empty'
  return b.verdict_count === 0 ? 'uncovered' : 'covered'
}

const STRIP_STYLES: Record<ReturnType<typeof coverageState>, string> = {
  covered: 'bg-primary/10 text-foreground',
  uncovered: 'border border-dashed border-destructive/60 text-destructive',
  empty: 'bg-muted text-muted-foreground',
}

function CoverageStrip({ buckets }: { buckets: TrendBucket[] }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-medium text-muted-foreground">
        Verdict coverage
      </h3>
      <div className="flex gap-1">
        {buckets.map((b) => {
          const state = coverageState(b)
          return (
            <div
              key={b.start}
              data-coverage-state={state}
              title={
                state === 'empty'
                  ? `${b.start}: no conversations`
                  : `${b.start}: ${b.verdict_count} of ${b.conversation_count} conversations analyzed`
              }
              className={`flex-1 rounded-sm px-1 py-0.5 text-center text-[10px] ${STRIP_STYLES[state]}`}
            >
              {state === 'empty' ? '—' : `${b.verdict_count}/${b.conversation_count}`}
            </div>
          )
        })}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Outlined buckets have unanalyzed conversations — run a report over
        that period to fill the gap.
      </p>
    </div>
  )
}

function TrendBarPanel({
  title, data, keys, colors, theme, height = 240,
}: {
  title: string
  data: BarRow[]
  keys: string[]
  colors: string[]
  theme: ReturnType<typeof useNivoTheme>
  height?: number
}) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-medium text-muted-foreground">{title}</h3>
      <div style={{ height }}>
        <ResponsiveBar
          // BarRow carries a `covered` boolean for our own bookkeeping
          // (not a chart key/value) — nivo's BarDatum is string|number-only,
          // so bridge the shape here rather than widen BarRow's contract.
          data={data as unknown as BarDatum[]}
          keys={keys}
          indexBy="bucket"
          theme={theme}
          colors={colors}
          margin={{ top: 12, right: 20, bottom: 60, left: 48 }}
          padding={0.3}
          axisBottom={{
            tickRotation: data.length > 8 ? -45 : 0,
            tickSize: 5,
            tickPadding: 5,
          }}
          axisLeft={{ tickSize: 5, tickPadding: 5 }}
          labelSkipHeight={12}
          motionConfig="gentle"
        />
      </div>
      {/* Plain HTML legend, not nivo's `legends` config — @nivo/bar is
          mocked in tests, so the legend must be rendered by our own code
          to be assertable. Cycling colors[i % colors.length] mirrors how
          nivo assigns series colors from the same array, so the legend
          swatches agree with the bars. */}
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
        {keys.map((k, i) => (
          <span key={k} className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: colors[i % colors.length] }}
            />
            <span className="text-[11px] text-muted-foreground">{k}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

export function TrendsTab() {
  const { personas } = usePersonas()
  const [start, setStart] = useState(() => {
    const now = new Date()
    // Trailing 12 full weeks including today.
    return toDateInput(
      new Date(now.getFullYear(), now.getMonth(), now.getDate() - 83),
    )
  })
  const [end, setEnd] = useState(() => toDateInput(new Date()))
  const [bucket, setBucket] = useState<'week' | 'month'>('week')
  const [channels, setChannels] = useState<string[]>([])
  const [personaIds, setPersonaIds] = useState<string[]>([])

  const trends = useAnalysisTrends({ start, end, bucket, channels, personaIds })
  const theme = useNivoTheme()
  const palette = useChartColors()

  const rangeInvalid = !start || !end || end < start
  const toggle = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

  const buckets = trends.data?.buckets ?? []
  const categories = trends.data?.categories ?? []
  const anyCovered = buckets.some((b) => (b.coverage ?? 0) > 0)

  return (
    <div className="space-y-4">
      <div className="rounded-sm border border-border bg-card p-4 space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor="trends-start" className="block text-xs text-muted-foreground mb-1">
              Start
            </label>
            <Input
              id="trends-start"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="trends-end" className="block text-xs text-muted-foreground mb-1">
              End
            </label>
            <Input
              id="trends-end"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="trends-bucket" className="block text-xs text-muted-foreground mb-1">
              Bucket
            </label>
            <select
              id="trends-bucket"
              value={bucket}
              onChange={(e) => setBucket(e.target.value as 'week' | 'month')}
              className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm text-foreground"
            >
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>
        </div>
        <fieldset>
          <legend className="text-xs text-muted-foreground mb-1">
            Channels (none = all)
          </legend>
          <div className="flex flex-wrap gap-3">
            {CHANNEL_OPTIONS.map((ch) => (
              <label key={ch} className="flex items-center gap-1.5 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={channels.includes(ch)}
                  onChange={() => setChannels((prev) => toggle(prev, ch))}
                />
                {ch}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="text-xs text-muted-foreground mb-1">
            Personas (none = all)
          </legend>
          <div className="max-h-36 overflow-y-auto space-y-1 rounded-sm border border-border p-2">
            {personas.map((p) => (
              <label key={p.id} className="flex items-center gap-1.5 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={personaIds.includes(p.id)}
                  onChange={() => setPersonaIds((prev) => toggle(prev, p.id))}
                />
                {p.name}
              </label>
            ))}
            {personas.length === 0 && (
              <p className="text-xs text-muted-foreground">No personas.</p>
            )}
          </div>
        </fieldset>
        {rangeInvalid && start && end && (
          <p className="text-xs text-destructive">
            End date must not be before start date.
          </p>
        )}
      </div>

      {trends.isLoading && (
        <p className="py-8 text-sm text-muted-foreground">Loading trends…</p>
      )}
      {trends.isError && (
        <p role="alert" className="py-8 text-sm text-destructive">
          Couldn't load trends — {analysisErrorMessage(trends.error)}
        </p>
      )}
      {trends.isSuccess && (
        <>
          {!anyCovered && (
            <p className="py-4 text-sm text-muted-foreground">
              No analyzed conversations in this period — run a report to
              generate verdicts, then trends appear here.
            </p>
          )}
          {anyCovered && (
            <>
              <TrendBarPanel
                title="Failure categories"
                data={toCategoryBarData(buckets, categories)}
                keys={categories}
                colors={palette}
                theme={theme}
              />
              <TrendBarPanel
                title="Outcome mix"
                data={toOutcomeBarData(buckets)}
                keys={OUTCOME_KEYS}
                colors={palette}
                theme={theme}
              />
            </>
          )}
          <CoverageStrip buckets={buckets} />
        </>
      )}
    </div>
  )
}
